<template>
  <BaseListCard
    title="Built Environment Features"
    :parentId="parentId"
    :parentTitle="'Property'"
    :options="{}"
    :entities="entities"
    :entitiesLoading="entitiesLoading"
    :entitySort="entitySort"
    :deleteEntity="deleteEntity"
    :emptyEntity="emptyEntity"
    ref="baseCard"
  >
    <template v-slot:cardSummary>
      <div class="card listing-card">
        <div class="card-body">
          <div class="row">
            <div class="col-lg-10">
              <h4>Summary</h4>
              <div class="table-responsive-sm">
                <table class="table rights-features-table"
                       aria-describedby="built environment features">
                  <thead>
                    <th></th>
                    <th>Allowed</th>
                    <th>Existing</th>
                    <th>Remaining</th>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="label-text">Dwelling Units (#)</td>
                      <td>{{ currentProperty.allowedDwellingUnits | numberFormatNullIsNA }}</td>
                      <td>
                        {{ currentProperty.existingDwellingUnits | numberFormat }}
                      </td>
                      <td
                          v-bind:class=
                          "{ 'text-danger':
                             isNegativeNumber(currentProperty.remainingDwellingUnits) }">
                        {{ currentProperty.remainingDwellingUnits | numberFormatNullIsNA }}
                      </td>
                    </tr>
                    <tr>
                      <td class="label-text">Finished Area (sq ft)</td>
                      <td>{{ currentProperty.allowedFinishedArea | numberFormatNullIsNA }}</td>
                      <td>
                        {{ currentProperty.existingFinishedArea | numberFormat }}
                      </td>
                      <td
                          v-bind:class=
                          "{ 'text-danger':
                              isNegativeNumber(currentProperty.remainingFinishedArea) }">
                        {{ currentProperty.remainingFinishedArea | numberFormatNullIsNA }}
                      </td>
                    </tr>
                    <tr>
                      <td class="label-text">Impervious Area (sq ft)</td>
                      <td>{{ currentProperty.allowedImperviousArea | numberFormatNullIsNA }}</td>
                      <td>
                        {{ currentProperty.existingImperviousArea | numberFormat }}
                      </td>
                      <td
                          v-bind:class=
                          "{ 'text-danger':
                              isNegativeNumber(currentProperty.remainingImperviousArea) }">
                        {{ currentProperty.remainingImperviousArea | numberFormatNullIsNA }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="col-lg-2 d-flex justify-content-end">
              <DownloadFile v-if="parentId"
                :entityId="parentId"
                :urlToDownloadFrom="`/property/${parentId}/BuiltEnvironmentExcel`"
                :buttonText="'Export Excel'"
                :contentType="contentType"
                :defaultFileName="defaultFileName"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:addForm="{ newEntity, options, stopAdding, entityCreated }">
      <BuiltEnvironmentForm
         :existingEntity="newEntity"
         @closeForm="stopAdding"
        :submitCallback="refetchProp">
      </BuiltEnvironmentForm>
    </template>
    <template v-slot:editForm="{ entity, options, stopEditing, entityUpdated }">
      <BuiltEnvironmentForm
        :existingEntity="entity"
        @closeForm="stopEditing"
        :submitCallback="refetchProp">
      </BuiltEnvironmentForm>
    </template>
    <template v-slot:entityDetails=" { entity }">
      <div class="row">
        <template v-if="entity.id">
          <div class="col-md-2">
            <div class="list-header">
              Feature ID
            </div>
            <div class="list-details">
              {{entity.id}}
            </div>
          </div>
        </template>
        <div class="col-md-3">
          <div class="list-header">
            Description
          </div>
          <div class="list-details">
            {{entity.description}}
          </div>
        </div>
        <div class="col-md-2">
          <div class="list-header">
            Is Building?
          </div>
          <div class="list-details">
            {{entity.isBuilding | formatBooleanAsYesNo}}
          </div>
        </div>
        <div class="col-md-2">
          <div class="list-header">
            Existing?
          </div>
          <div class="list-details">
            {{entity.exists | formatBooleanAsYesNo}}
          </div>
        </div>
        <div class="col-md-2">
          <div class="list-header">
            Dwelling Units
          </div>
          <div class="list-details">
            {{entity.dwellingUnits | numberFormat}}
          </div>
        </div>
      </div>
      <hr>
      <div class="row">
        <div class="col-md-2">
            <div class="list-header">
            Finished Area Square Feet
          </div>
          <div class="list-details">
            {{entity.finishedArea | numberFormat}}
          </div>
        </div>
        <div class="col-md-3">
           <div class="list-header">
            Impervious Area Square Feet
          </div>
          <div class="list-details">
            {{entity.imperviousArea | numberFormat}}
          </div>
        </div>
        <div class="col-md-3">
          <div class="list-header">
            Primary Source
          </div>
          <div class="list-details">
            {{getPrimarySourceName(entity.primarySourceId)}}
          </div>
        </div>
        <div class="col-md-3">
          <div class="list-header">
            Last Mod
          </div>
          <div class="list-details">
            {{entity.updatedAt | dateString}}
          </div>
        </div>
      </div>
      <hr>
      <div class="row">
        <div class="col-md-12">
          <div class="list-header">
            Feature Notes
          </div>
          <div class="list-details notes-field">{{ entity.notes || 'None' }}</div>
        </div>
      </div>
    </template>
    <template v-slot:additionalActions="{ entity }">
      <MoveBEModal :entity="entity" :submitCallback="refetchProp" />
    </template>
  </BaseListCard>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import BaseListCard from '../common/BaseListCard.vue';
import BuiltEnvironmentForm from './BuiltEnvironmentForm.vue';
import CollapseParent from '@/mixins/CollapseParent';
import MoveBEModal from './MoveBEModal.vue';
import { EntityModules, LookupModules } from '@/store/moduleConfig';
import DownloadFile from '../common/DownloadFile.vue';


export default {
  name: 'BuiltEnvironmentFeatureCard',
  props: [
    'parentId',
  ],
  mixins: [CollapseParent],
  data() {
    return {
      contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      defaultFileName: `Property ${this.parentId} Development Rights Summary and BE Features.xlsx`,
    };
  },
  components: {
    BaseListCard,
    BuiltEnvironmentForm,
    MoveBEModal,
    DownloadFile,
  },
  computed: {
    ...mapState(EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace, {
      entities: state => state.entities,
      entitiesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.PRIMARY_SOURCES.namespace, {
      primarySourcesLoading: state => state.loading,
    }),
    ...mapState({
      currentProperty: state => state.Property.currentProperty,
    }),
    ...mapGetters(LookupModules.PRIMARY_SOURCES.namespace, {
      primarySourceOptions: 'getActiveOptions',
    }),
  },
  created() {
    this.$store.dispatch(`${LookupModules.PRIMARY_SOURCES.namespace}/fetchOptions`,
      LookupModules.PRIMARY_SOURCES.url);
  },
  methods: {
    entitySort(a, b) {
      if (a.exists === b.exists) {
        if (b.dwellingUnits === a.dwellingUnits) {
          return a.id - b.id;
        }
        return b.dwellingUnits - a.dwellingUnits;
      }
      return b.exists - a.exists;
    },
    emptyEntity() {
      return {
        propertyId: this.parentId,
        description: '',
        isBuilding: true,
        exists: true,
        dwellingUnits: 0,
        imperviousArea: 0,
        finishedArea: 0,
        primarySourceId: null,
        notes: null,
      };
    },
    getPrimarySourceName(id) {
      if (this.primarySourceOptions && this.primarySourceOptions.some(ps => ps.id === id)) {
        return this.primarySourceOptions.filter(ps => ps.id === id)[0].name;
      }
      return '';
    },
    deleteEntity(entity) {
      this.$store.dispatch(
        `${EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace}/deleteEntity`, {
          entityType: EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace,
          entity,
        },
      ).then(this.refetchProp);
    },
    isNegativeNumber(amount) {
      if (amount) return (Number(amount) < 0);
      return false;
    },
    refetchProp() {
      this.$store.dispatch('Property/refetchCurrentProperty');
      // need to make sure that the development rights summary on the project gets the changes too
      this.$store.dispatch('CurrentProject/fetchDevelopmentRightsSummary', {
        projectId: parseInt(this.$route.params.project_id, 10),
        forceFetch: true,
      });
      this.$store.dispatch(
        `${EntityModules.BUILT_ENVIRONMENT_FEATURE.namespace}/fetchEntities`, {
          parentType: EntityModules.BUILT_ENVIRONMENT_FEATURE.parentType,
          parentId: parseInt(this.$route.params.property_id, 10),
          entityType: EntityModules.BUILT_ENVIRONMENT_FEATURE.entityType,
        },
      ); 
    },
  },
};
</script>
